import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Activity, ShieldCheck, TrendingUp, MessageCircle, CalendarCheck, Megaphone, AlertTriangle, ExternalLink, RadioTower } from "lucide-react";

const KIND_ICON = {
  real_match: ShieldCheck,
  activity: TrendingUp,
  sourcing: ShieldCheck,
  pipeline: MessageCircle,
  campaigns: Megaphone,
  warning: AlertTriangle,
  example: Activity,
};

const KIND_ACCENT = {
  real_match: "text-emerald-300",
  activity: "text-gold",
  sourcing: "text-emerald-300",
  pipeline: "text-blue-300",
  campaigns: "text-purple-300",
  warning: "text-amber-300",
  example: "text-zinc-400",
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function SignalFeed() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const timerRef = useRef(null);

  const load = async (soft = false) => {
    if (!soft) setError("");
    setRefreshing(true);
    try {
      const { data } = await api.get("/signals");
      setData(data);
    } catch (e) {
      if (!soft) setError("Could not load signals.");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(false);
    timerRef.current = setInterval(() => load(true), 30000);
    return () => timerRef.current && clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signals = data?.signals || [];
  const totals = data?.totals || {};

  return (
    <div className="glass-card p-6" data-testid="signal-feed">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="label-eyebrow mb-1 flex items-center gap-2">
            <RadioTower className="w-3 h-3 text-emerald-400" />
            Live signal feed
            {refreshing && <span className="text-[10px] text-zinc-500 normal-case">refreshing…</span>}
          </div>
          <div className="font-display text-xl text-white">What's happening in your workspace</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-display text-emerald-300" data-testid="signal-real-count">{totals.real_leads ?? 0}</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">real orgs</div>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-sm text-rose-300 mb-4" data-testid="signal-feed-error">{error}</div>
      )}

      <div className="space-y-2">
        {signals.length === 0 && !error && (
          <div className="text-sm text-zinc-500 p-4 text-center" data-testid="signal-feed-empty">
            No signals yet — mine some real leads to activate the feed.
          </div>
        )}
        {signals.map((s) => {
          const Icon = KIND_ICON[s.kind] || Activity;
          const accent = KIND_ACCENT[s.kind] || "text-zinc-400";
          return (
            <div
              key={s.id}
              className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                s.example
                  ? "bg-white/[0.01] border-white/5 opacity-70"
                  : "bg-white/[0.02] border-white/5 hover:bg-white/[0.04]"
              }`}
              data-testid={`signal-${s.id}`}
            >
              <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${accent}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white leading-snug flex items-center gap-2 flex-wrap">
                  <span>{s.message}</span>
                  {s.example && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-700/40 text-zinc-400 border border-zinc-600/30 uppercase tracking-wider">example</span>
                  )}
                </div>
                {s.detail && <div className="text-xs text-zinc-500 mt-0.5">{s.detail}</div>}
              </div>
              <div className="flex-shrink-0 flex items-center gap-2 text-xs text-zinc-500">
                <span>{timeAgo(s.created_at)}</span>
                {s.link && (
                  <a href={s.link} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-emerald-300" data-testid={`signal-link-${s.id}`}>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
